import { useTsController } from "@ts-react/form";
import type React from "react";
import { useId } from "react";
import { useFormContext } from "react-hook-form";
import { Card, type CardProps, Text, XStack, YStack } from "tamagui";
import { Paragraph2 } from "../Typography";
import { defaultCardHeight, defaultCardWidth, defaultContainerWidth, defaultContainerWidthDesktop, defaultContainerWidthTablet } from "./constants";
import { Fieldset } from "./Fieldset";
import { FormError } from "./FormError";
export type CardSelectOption = {
  id: string;
  icon: string | React.ReactNode;
  label: string;
};
export type CardSelectItemProps = {
  id: string;
  icon: string | React.ReactNode;
  label: string;
  isSelected?: boolean;
  width: number;
  height: number;
} & CardProps;
export const CardSelectItem = ({
  id,
  icon,
  label,
  isSelected,
  width,
  height,
  ...props
}: CardSelectItemProps) => {
  return <Card id={id} width={width} height={height} borderColor={isSelected ? "$borderColorFocus" : "$borderColor"} borderWidth={isSelected ? "$1" : "$0.5"} padding={isSelected ? 23 : "$5"} cursor="pointer" $gtMd={{
    height: height + 20
  }} {...props} data-sentry-element="Card" data-sentry-component="CardSelectItem" data-sentry-source-file="CardSelectField.tsx">
      <YStack gap="$3" alignItems="center" justifyContent="center" data-sentry-element="YStack" data-sentry-source-file="CardSelectField.tsx">
        {typeof icon === "string" ? <Text fontSize="$7" textAlign="center">
            {icon}
          </Text> : icon}
        <Paragraph2 textAlign="center" data-sentry-element="Paragraph2" data-sentry-source-file="CardSelectField.tsx">{label}</Paragraph2>
      </YStack>
    </Card>;
};
export type CardSelectFieldProps = {
  native?: boolean;
  onSelect?: () => Promise<void>;
};
export const CardSelectField = ({
  options,
  onSelect
}: {
  options: CardSelectOption[];
  onSelect?: () => Promise<void>;
} & CardSelectFieldProps) => {
  const {
    field,
    error,
    formState: {
      isSubmitting
    }
  } = useTsController<string>();
  const {
    resetField
  } = useFormContext();
  const id = useId();
  const disabled = isSubmitting;
  async function handleSelect(option: string) {
    // marks the field as dirty
    resetField(field.name);
    field.onChange(option);
    await onSelect?.();
  }
  return <Fieldset data-sentry-element="Fieldset" data-sentry-component="CardSelectField" data-sentry-source-file="CardSelectField.tsx">
      <XStack flex={1} alignContent="center" justifyContent="center" data-sentry-element="XStack" data-sentry-source-file="CardSelectField.tsx">
        <XStack id={id} gap="$3" flexWrap="wrap" justifyContent="flex-start" paddingTop="$5" paddingBottom="$10" maxWidth={defaultContainerWidth} $gtSm={{
        maxWidth: defaultContainerWidthTablet
      }} $gtMd={{
        maxWidth: defaultContainerWidthDesktop
      }} data-sentry-element="XStack" data-sentry-source-file="CardSelectField.tsx">
          {options.map(option => {
          return <CardSelectItem width={defaultCardWidth} height={defaultCardHeight} disabled={disabled} key={option.id} label={option.label} id={option.id} icon={option.icon} onPress={() => {
            void handleSelect(option.id);
          }} isSelected={field.value === option.id} />;
        })}
        </XStack>
      </XStack>
      <FormError errorMessage={error?.errorMessage} data-sentry-element="FormError" data-sentry-source-file="CardSelectField.tsx" />
    </Fieldset>;
};